import { useCallback, useMemo, useState } from "react";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { AbsenceType } from "../../../utils/models";

/**
 * State container for the scheduler call-off / nurse-removal dialog.
 *
 * The reason value is the literal label string that gets persisted onto `nurse_in_shift.message`
 * (same shape as the historical AbsenceType enum). The picker is driven by the department's
 * `department_call_in_reason` rows (look-up done at the call site).
 *
 * `hospitalRecordsCallIn` is the caller's explicit choice for hospitals: on, a hospital skips the picker and
 * records a call-in (the nurse-menu delete and the mobile Gaps sheet keep that legacy behavior); off, every
 * department picks a reason, with nothing pre-selected (the desktop Remove Staff panel).
 *
 * The free-text `removalMessage` (writes to `removal_message`) is unchanged — that's the
 * scheduler's optional notes box and is rendered alongside the reason.
 */
export const useRemovalReasonState = ({ hospitalRecordsCallIn }: { hospitalRecordsCallIn: boolean }) => {
	const { isHospital } = useDepartmentFlags();
	const dontRenderReasonSelector = hospitalRecordsCallIn && isHospital;
	const [selectedReason, setSelectedReason] = useState<string | undefined>(dontRenderReasonSelector ? AbsenceType.CALL_IN : undefined);
	const [removalMessage, setRemovalMessage] = useState<string>("");

	const handleReasonChange = useCallback((value: string | undefined) => {
		if (value) {
			setSelectedReason(value);
		}
	}, []);

	const isReasonValid = useMemo(() => !!selectedReason, [selectedReason]);

	const getFullReason = useCallback(() => selectedReason ?? "", [selectedReason]);

	return {
		selectedReason,
		handleReasonChange,
		removalMessage,
		setRemovalMessage,
		isReasonValid,
		getFullReason,
		dontRenderReasonSelector,
	};
};
